/**
 * AccettaRichiestaButton — bottone + modal per accettare una richiesta fornitore
 * generando le rate secondo le condizioni di pagamento.
 * Usato nella lista richieste (globale e tab cantiere).
 */
import React from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, X } from "lucide-react";
import api from "@/api";
import { COND_PAGAMENTO } from "@/pages/RichiesteMateriali";

export function AccettaRichiestaModal({ richiesta, onClose, onDone }) {
  const [nota, setNota] = React.useState("");
  const [cond, setCond] = React.useState(richiesta.condizioni_pagamento || "30");
  const [dataAcc, setDataAcc] = React.useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = React.useState([]);

  React.useEffect(() => {
    const params = new URLSearchParams({
      totale_imponibile: String(richiesta.totale_valore || 0),
      aliquota_iva: String(richiesta.aliquota_iva || 22),
      condizioni_pagamento: cond,
      data_riferimento: dataAcc,
    });
    api.get(`/richieste-materiali/preview-rate?${params.toString()}`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview([]));
  }, [cond, dataAcc, richiesta.totale_valore, richiesta.aliquota_iva]);

  const accept = useMutation({
    mutationFn: () => api.post(`/richieste-materiali/${richiesta.richiesta_id}/accetta`, {
      nota_fornitore: nota,
      condizioni_pagamento: cond,
      data_accettazione: dataAcc,
    }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Richiesta ${richiesta.numero_richiesta} accettata · ${(data.rate_pagamento || []).length} rate create in Contabilità`);
      onDone?.(data);
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore accettazione"),
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-accetta-richiesta">
      <div className="bg-white rounded-md shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto border-2 border-emerald-400" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-emerald-200 bg-emerald-50 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Accetta richiesta fornitore</div>
            <h3 className="font-display font-bold text-lg text-slate-900">{richiesta.numero_richiesta} · {richiesta.fornitore_nome}</h3>
            <div className="text-xs text-slate-500 mt-0.5">{richiesta.cantiere_nome} · Totale € {(richiesta.totale_valore || 0).toFixed(2)}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white rounded"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Data accettazione</label>
              <input type="date" value={dataAcc} onChange={(e) => setDataAcc(e.target.value)} data-testid="input-data-accettazione" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              <div className="text-[10px] text-slate-500 mt-1">Le scadenze partono da questa data.</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Condizioni di pagamento</label>
              <select value={cond} onChange={(e) => setCond(e.target.value)} data-testid="select-condizioni-accetta" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                {COND_PAGAMENTO.filter((c) => c.key !== "personalizzato").map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Nota (facoltativa)</label>
            <textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Confermato via telefono · Sconto 5% applicato · ecc." className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="input-nota-accetta" />
          </div>

          <div className="border border-emerald-200 rounded-md overflow-hidden">
            <div className="bg-emerald-100 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-emerald-800">
              📅 Anteprima scadenze in Contabilità
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Scadenza</th>
                  <th className="text-right p-2">Imponibile</th>
                  <th className="text-right p-2">IVA</th>
                  <th className="text-right p-2">Totale</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r) => (
                  <tr key={r.numero} className="border-t border-slate-100" data-testid={`preview-rata-${r.numero}`}>
                    <td className="p-2 font-mono">{r.numero}/{r.totale_rate}</td>
                    <td className="p-2 font-mono">{r.data_scadenza}<span className="text-slate-400 ml-1">(+{r.giorni}gg)</span></td>
                    <td className="p-2 text-right font-mono">€ {r.importo.toFixed(2)}</td>
                    <td className="p-2 text-right font-mono text-slate-500">€ {(r.importo_con_iva - r.importo).toFixed(2)}</td>
                    <td className="p-2 text-right font-mono font-bold text-emerald-800">€ {r.importo_con_iva.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm text-[11px] text-amber-900">
            All'accettazione verranno creati <b>{preview.length}</b> record in <b>Costi Variabili · Fornitori</b> (uno per rata), collegati al cantiere <b>{richiesta.cantiere_nome}</b>.
          </div>
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white">Annulla</button>
          <button
            onClick={() => accept.mutate()}
            disabled={accept.isPending || preview.length === 0}
            data-testid="btn-conferma-accetta"
            className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold rounded-sm inline-flex items-center gap-1"
          >
            <CheckCircle2 className="w-4 h-4" />
            {accept.isPending ? "Accettazione…" : "Accetta e genera rate"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RifiutaRichiestaButton({ richiesta, onDone }) {
  const [confirming, setConfirming] = React.useState(false);
  const [nota, setNota] = React.useState("");
  const reject = useMutation({
    mutationFn: () => api.post(`/richieste-materiali/${richiesta.richiesta_id}/rifiuta`, { nota_fornitore: nota }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Richiesta ${richiesta.numero_richiesta} rifiutata`);
      onDone?.(data);
      setConfirming(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore rifiuto"),
  });
  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        data-testid={`btn-rifiuta-${richiesta.numero_richiesta}`}
        title="Rifiuta richiesta"
        className="p-1 text-rose-700 hover:bg-rose-50 border border-rose-200 rounded-sm inline-flex items-center gap-1"
      >
        <X className="w-3.5 h-3.5" />
        <span className="text-[11px] font-semibold">Rifiuta</span>
      </button>
    );
  }
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirming(false)}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-rose-200 bg-rose-50">
          <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800">Rifiuta richiesta</div>
          <h3 className="font-display font-bold text-slate-900">{richiesta.numero_richiesta} · {richiesta.fornitore_nome}</h3>
        </div>
        <div className="p-4">
          <label className="text-xs font-semibold text-slate-600 block mb-1">Motivo (facoltativo)</label>
          <textarea rows={3} value={nota} onChange={(e) => setNota(e.target.value)} data-testid="input-nota-rifiuta" placeholder="Prezzo troppo alto · Cambio fornitore · ecc." className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          {richiesta.stato === "accettata" && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded-sm text-[11px] text-amber-900">
              ⚠️ Questa richiesta era già accettata: le rate non ancora pagate saranno azzerate. Le rate <b>già pagate</b> restano invariate.
            </div>
          )}
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={() => setConfirming(false)} className="px-3 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white">Annulla</button>
          <button onClick={() => reject.mutate()} disabled={reject.isPending} data-testid="btn-conferma-rifiuta" className="px-3 py-2 text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-sm">
            {reject.isPending ? "…" : "Rifiuta richiesta"}
          </button>
        </div>
      </div>
    </div>
  );
}
